import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  CalendarClock,
  HeartHandshake,
  Info,
  MessagesSquare,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import RelatedLinks from "@/components/common/RelatedLinks";
import { HEYCATCH_EVENTS, trackOutcomeOncePerSession } from "@/lib/heycatch";

/**
 * /vs/ai-sponsor - an informational comparison page.
 *
 * Editorial rules baked into this copy (do not loosen without the owner's sign-off):
 *  - Both approaches are described factually and with equal visual weight. We do
 *    not claim AI recovery tools are unsafe, ineffective or inferior, and we do
 *    not claim Sponsor Me produces better recovery outcomes.
 *  - We never imply every human sponsor has the same experience, availability,
 *    qualifications or results.
 *  - Every statement about Second Sponsor is taken from their own public
 *    materials (see COMPETITOR_SOURCES below) and nothing is paraphrased into a
 *    stronger claim than the source supports.
 *
 * SEO title/description/canonical/Open Graph/Twitter and the JSON-LD graph are
 * NOT set here: the site's central SEO system does that from
 * src/lib/seo-pages.json via applySeo() in App.js, so this page cannot end up
 * with duplicate metadata or schema.
 */

// Verified against Second Sponsor's own public materials on 2026-09-19.
// Kept next to the copy so any future edit can be re-checked against a source.
const COMPETITOR_SOURCES = {
  site: "https://secondsponsor.com/",
  features: "https://secondsponsor.com/features",
  appStore: "https://apps.apple.com/us/app/second-sponsor/id6737695789",
};

const COMPARISON = [
  {
    id: "human-relationship",
    aspect: "Who you are talking to",
    icon: Users,
    sponsorMe: "A real volunteer peer sponsor - another person in recovery who chose to make themselves available.",
    ai: "Conversation and guidance generated by software, available inside an app.",
  },
  {
    id: "shared-experience",
    aspect: "Shared recovery experience",
    icon: HeartHandshake,
    sponsorMe:
      "A sponsor may be able to share personal experience from their own recovery where it is relevant. What they have lived through, and how much they share, varies from person to person.",
    ai: "Can provide information, structure and conversational support drawn from recovery literature, but does not personally go through recovery.",
  },
  {
    id: "availability",
    aspect: "Availability",
    icon: CalendarClock,
    sponsorMe:
      "People have their own lives, jobs and schedules. A sponsor may not be free the moment you reach out, and response times differ between individuals.",
    ai: "Automated tools can offer around-the-clock availability, so something is there at 3am.",
  },
  {
    id: "relationship-type",
    aspect: "Type of relationship",
    icon: MessagesSquare,
    sponsorMe:
      "You send a sponsorship request and the sponsor chooses whether to accept, so an actual two-way peer relationship is formed by mutual agreement.",
    ai: "Generally a digital interaction you can start and stop at will, rather than a human sponsorship relationship with another person.",
  },
  {
    id: "consistency",
    aspect: "Consistency and what it offers",
    icon: Sparkles,
    sponsorMe:
      "Interpersonal connection, another person's perspective, accountability to someone who knows you, and conversation with a human being.",
    ai: "Immediate, consistent, always-the-same responses, plus features like tracking and prompts that do not depend on anyone else's schedule.",
  },
];

export default function VsAiSponsor() {
  useEffect(() => {
    // Once per browser session, so React re-renders, scroll-triggered updates and
    // re-navigating back to this page cannot inflate the count.
    trackOutcomeOncePerSession(
      "vs_ai_sponsor_viewed",
      HEYCATCH_EVENTS.VS_AI_SPONSOR_VIEWED
    );
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="vs-ai-sponsor-page">
      {/* Hero */}
      <header>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D7EEF0] text-xs font-bold uppercase tracking-wide text-[#1F6F78]">
          <Info className="w-3.5 h-3.5" /> Comparison
        </span>
        <h1
          data-testid="vs-ai-heading"
          className="mt-4 font-display text-3xl sm:text-4xl font-bold text-[#1F2A2E] leading-tight"
        >
          Human Sponsor vs. AI Recovery Companion: What&apos;s the Difference?
        </h1>
        <p data-testid="vs-ai-definition" className="mt-4 text-[17px] text-[#1F2A2E] leading-8 font-medium">
          An AI recovery companion uses software to provide digital guidance, while Sponsor Me helps
          people connect with real volunteer peer sponsors.
        </p>
        <p className="mt-3 text-[17px] text-[#3A464B] leading-8">
          AI recovery tools can offer convenient, always-available guidance, prompts, and
          information. Sponsor Me takes a different approach: helping people connect with real
          volunteer peer sponsors for human-to-human recovery support.
        </p>
        <p className="mt-3 text-[17px] text-[#3A464B] leading-8">
          These are not really rivals. Plenty of people use an app for daily structure and also
          want a person they can talk to, and the two can sit alongside each other. This page is
          here to explain the difference plainly so you can decide what you are actually looking
          for right now.
        </p>
      </header>

      {/* Comparison */}
      <section className="mt-10" aria-labelledby="comparison-heading">
        <h2
          id="comparison-heading"
          className="font-display text-2xl font-bold text-[#1F2A2E]"
        >
          Two different kinds of support, side by side
        </h2>
        <p className="mt-2 text-[#4B5A60] leading-7">
          A factual comparison of peer sponsorship on Sponsor Me and AI recovery companions in
          general. Individual sponsors and individual apps vary, so treat this as the shape of the
          difference rather than a promise about any one person or product.
        </p>

        {/* Column headers - desktop only; each card repeats them on mobile */}
        <div className="mt-6 hidden md:grid md:grid-cols-[1fr_1fr] gap-4">
          <div className="rounded-t-2xl bg-[#1F6F78] px-5 py-3 flex items-center gap-2">
            <HeartHandshake className="w-4 h-4 text-white shrink-0" />
            <p className="font-display text-base font-bold text-white">
              Sponsor Me / human peer sponsorship
            </p>
          </div>
          <div className="rounded-t-2xl bg-[#3A464B] px-5 py-3 flex items-center gap-2">
            <Bot className="w-4 h-4 text-white shrink-0" />
            <p className="font-display text-base font-bold text-white">AI recovery companions</p>
          </div>
        </div>

        <ul className="mt-4 md:mt-2 space-y-5">
          {COMPARISON.map((row) => (
            <li key={row.id} data-testid={`vs-row-${row.id}`} className="sm-card bg-white p-5">
              <div className="flex items-center gap-2">
                <row.icon className="w-4 h-4 text-[#1F6F78] shrink-0" />
                <h3 className="font-display text-lg font-bold text-[#1F2A2E]">{row.aspect}</h3>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="min-w-0 rounded-xl bg-[#F2FAFA] border border-[#CFE6E8] p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#1F6F78]">
                    Sponsor Me
                  </p>
                  <p className="mt-1.5 text-sm text-[#1F2A2E] leading-6">{row.sponsorMe}</p>
                </div>
                <div className="min-w-0 rounded-xl bg-[#F4F3F1] border border-[#DDD9D2] p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                    AI recovery companion
                  </p>
                  <p className="mt-1.5 text-sm text-[#1F2A2E] leading-6">{row.ai}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Which one are you looking for */}
      <section className="mt-10 rounded-2xl bg-[#F2EFE8] border border-[#E3DED4] p-6">
        <h2 className="font-display text-xl font-bold text-[#1F2A2E]">
          So which one are you looking for?
        </h2>
        <p className="mt-3 text-[#3A464B] leading-7">
          If what you want is something available the instant you need it, that tracks your
          progress and never has an off day, an AI recovery tool may suit that well. If what you
          want is another person who has walked a recovery path and can talk with you about it,
          that is what Sponsor Me is built for. Some people want both, and there is nothing
          contradictory about that.
        </p>
      </section>

      {/* Second Sponsor - verified facts only */}
      <section className="mt-10" aria-labelledby="second-sponsor-heading">
        <h2
          id="second-sponsor-heading"
          className="font-display text-2xl font-bold text-[#1F2A2E]"
        >
          How Sponsor Me compares with Second Sponsor
        </h2>
        <div className="mt-4 sm-card bg-white p-6">
          <p className="text-[#3A464B] leading-7">
            Second Sponsor is one product people may come across when they search for recovery
            support, so it is worth explaining plainly. Its own App Store listing describes it as
            &quot;an AI-assisted companion for individuals in 12-step recovery programs,&quot; and
            its website describes features including 24/7 AI guidance for step work, a meeting
            finder with GPS-verified check-ins, daily check-in and journaling prompts, a meditation
            library, and detection of high-risk language with escalation. It is published by Second
            Sponsor LLC and listed for iOS and Android.
          </p>
          <p className="mt-4 text-[#3A464B] leading-7">
            Second Sponsor&apos;s own listing also states that it &quot;is not a substitute for
            professional mental health or medical care,&quot; that it &quot;does not provide medical
            advice or diagnoses of any kind,&quot; and that &quot;users should seek licensed
            professionals for mental health or medical treatment.&quot;
          </p>
          <p className="mt-4 text-[#3A464B] leading-7">
            <strong className="text-[#1F2A2E]">Sponsor Me serves a different purpose.</strong>{" "}
            Sponsor Me helps people find and connect with real volunteer peer sponsors. Browsing is
            free, sponsors use the platform free, and you reach out to a person who then decides
            whether to accept. We are not an AI companion and we do not try to be one.
          </p>
          <p className="mt-4 text-xs text-[#7A868B] leading-6" data-testid="vs-ai-non-affiliation">
            Sponsor Me is not affiliated with, endorsed by, or sponsored by Second Sponsor. Details
            above were taken from Second Sponsor&apos;s own public website and App Store listing and
            were last checked on 19 September 2026; products change, so check their current
            materials for the latest information.
          </p>
        </div>
      </section>

      {/* Sponsor Me disclaimer */}
      <section className="mt-8 rounded-2xl border border-[#E7CF9E] bg-[#FBF0DC] p-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#8A5A12] shrink-0" />
          <h2 className="font-display text-xl font-bold text-[#8A5A12]">
            What Sponsor Me is, and what it is not
          </h2>
        </div>
        <p className="mt-3 text-sm text-[#8A5A12] leading-7" data-testid="vs-ai-disclaimer">
          Sponsor Me is a peer-to-peer connection platform. It does not provide therapy, medical
          care, professional counseling, addiction treatment, or emergency services. Sponsor Me is
          an independent service and is not affiliated with, endorsed by, or approved by Alcoholics
          Anonymous, Narcotics Anonymous, SMART Recovery, Crystal Meth Anonymous, or any other
          recovery fellowship. Sponsors and members are independent users of the platform.
        </p>
        <p className="mt-3 text-sm text-[#8A5A12] leading-7">
          If you are in crisis or immediate danger, please use the{" "}
          <Link to="/resources" className="font-semibold underline">
            emergency recovery resources
          </Link>{" "}
          instead of waiting for a reply from anyone.
        </p>
      </section>

      {/* CTA */}
      <section className="mt-10 rounded-2xl bg-[#1F2A2E] p-8 text-[#F7F4EF]">
        <h2 className="font-display text-2xl font-bold">
          Looking for a real person to connect with in recovery?
        </h2>
        <p className="mt-3 text-[#CBD6D8] leading-7 max-w-2xl">
          Browse volunteer peer sponsors and find someone whose recovery experience and approach may
          be a good fit for you. Sponsors join voluntarily and choose which requests to accept, so
          take your time and reach out to someone who sounds right for you.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button
            asChild
            onClick={() =>
              trackOutcomeOncePerSession(
                "vs_ai_browse_sponsors_clicked",
                HEYCATCH_EVENTS.VS_AI_BROWSE_SPONSORS_CLICKED
              )
            }
            className="h-12 px-6 bg-[#1F6F78] hover:bg-[#185B62] text-white"
          >
            <Link to="/sponsors" data-testid="vs-ai-browse-sponsors-cta">
              Browse Sponsors
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            onClick={() =>
              trackOutcomeOncePerSession(
                "vs_ai_become_sponsor_clicked",
                HEYCATCH_EVENTS.VS_AI_BECOME_SPONSOR_CLICKED
              )
            }
            className="h-12 px-6 bg-transparent border-[#4B5A60] text-[#F7F4EF] hover:bg-[#2C383D] hover:text-white"
          >
            <Link to="/become-a-sponsor" data-testid="vs-ai-become-sponsor-cta">
              Become a Sponsor
            </Link>
          </Button>
        </div>
      </section>

      {/* Internal links */}
      <nav className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-sm" aria-label="Related pages">
        <Link to="/about" className="font-semibold text-[#1F6F78] hover:underline">
          Why Sponsor Me was built
        </Link>
        <Link to="/resources" className="font-semibold text-[#1F6F78] hover:underline">
          Recovery resources
        </Link>
        <Link to="/pricing" className="font-semibold text-[#1F6F78] hover:underline">
          How membership works
        </Link>
      </nav>
      <RelatedLinks
        heading="Keep reading"
        links={[
          { to: "/sponsors", label: "Browse human peer sponsors" },
          { to: "/about", label: "Learn about Sponsor Me" },
          { to: "/become-a-sponsor", label: "Become a sponsor" },
          { to: "/resources", label: "Recovery resources" },
        ]}
      />
    </div>
  );
}

export { COMPETITOR_SOURCES };
